import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { ETLayout, euro, QuotaBadge } from "@/pages/et/ETLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Layers, Search, Wallet, Send, FileText, CheckCircle2, Undo2, UserPlus, AlertTriangle, Loader2, Mail } from "lucide-react";

const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function ETQuote() {
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [competenza, setCompetenza] = useState(thisMonth());
  const [statoFilter, setStatoFilter] = useState("all");
  const [query, setQuery] = useState("");

  // Genera massiva (flusso anteprima → conferma → risultato)
  const [genOpen, setGenOpen] = useState(false);
  const [genStep, setGenStep] = useState("input"); // input | preview | result
  const [genMonth, setGenMonth] = useState(thisMonth());
  const [genPreview, setGenPreview] = useState(null);
  const [genResult, setGenResult] = useState(null);
  const [genLoading, setGenLoading] = useState(false);

  // Genera singola
  const [singOpen, setSingOpen] = useState(false);
  const [singMonth, setSingMonth] = useState(thisMonth());
  const [singBen, setSingBen] = useState("");
  const [beneficiari, setBeneficiari] = useState([]);
  const [singLoading, setSingLoading] = useState(false);

  const [incasso, setIncasso] = useState(null);
  const [inc, setInc] = useState({ importo: "", data: new Date().toISOString().slice(0, 10), metodo: "bonifico", note: "" });
  const [annulla, setAnnulla] = useState(null);
  const [motivo, setMotivo] = useState("Sollecito inserito per errore");

  // Invio ricevuta post-incasso
  const [justPaid, setJustPaid] = useState(null);
  const [paidTo, setPaidTo] = useState("");
  const [sendingPaid, setSendingPaid] = useState(false);
  const [emailCfg, setEmailCfg] = useState({ configured: false, sender: "" });
  useEffect(() => { api.get("/et/email/status").then(({ data }) => setEmailCfg(data)).catch(() => {}); }, []);

  const load = () => {
    const params = {};
    if (competenza) params.competenza = competenza;
    if (statoFilter !== "all") params.stato = statoFilter;
    if (query) params.query = query;
    api.get("/et/quote", { params }).then(({ data }) => setItems(data));
  };
  useEffect(load, [competenza, statoFilter]); // eslint-disable-line

  // ---- Genera massiva -----------------------------------------------------
  const openGen = () => { setGenStep("input"); setGenPreview(null); setGenResult(null); setGenMonth(thisMonth()); setGenOpen(true); };

  const analizzaMassiva = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.get("/et/quote/genera-massiva/preview", { params: { competenza: genMonth } });
      setGenPreview(data); setGenStep("preview");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGenLoading(false); }
  };

  const analizzaMese = async (mese) => {
    setGenStep("input"); setGenPreview(null); setGenResult(null); setGenMonth(mese); setGenOpen(true);
    setGenLoading(true);
    try {
      const { data } = await api.get("/et/quote/genera-massiva/preview", { params: { competenza: mese } });
      setGenPreview(data); setGenStep("preview");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGenLoading(false); }
  };

  useEffect(() => {
    const m = location.state?.openGen;
    if (m) { analizzaMese(m); navigate(location.pathname, { replace: true, state: {} }); }
  }, []); // eslint-disable-line

  const confermaMassiva = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/et/quote/genera-massiva", { competenza: genMonth });
      setGenResult(data); setGenStep("result");
      toast.success(`${data.quote_create} quote generate`);
      setCompetenza(genMonth); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGenLoading(false); }
  };

  // ---- Genera singola -----------------------------------------------------
  const openSingola = () => {
    setSingMonth(competenza || thisMonth()); setSingBen(""); setSingOpen(true);
    api.get("/et/beneficiari", { params: { stato: "attivo" } }).then(({ data }) => setBeneficiari(data)).catch(() => {});
  };

  const generaSingola = async () => {
    if (!singBen) return;
    setSingLoading(true);
    try {
      const { data } = await api.post("/et/quote/genera-singola", { ben_id: singBen, competenza: singMonth });
      toast.success(`Quota creata per ${data.quota.ben_name} · ${euro(data.quota.quota)}`);
      setSingOpen(false); setCompetenza(singMonth); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSingLoading(false); }
  };

  const openIncasso = (q) => {
    const dovuto = (q.quota - (q.importo_pagato || 0)).toFixed(2);
    setIncasso(q); setInc({ importo: dovuto, data: new Date().toISOString().slice(0, 10), metodo: "bonifico", note: "" });
  };
  const doIncasso = async () => {
    try {
      const { data } = await api.post("/et/incassa", { quota_id: incasso.quota_id, importo: Number(inc.importo), data: inc.data, metodo: inc.metodo, note: inc.note });
      const msg = { PAGATO: "Quota saldata", PARZIALE: `Parziale: residuo ${euro(data.residuo)} generato`, ECCEDENZA: `Eccedenza ${euro(data.eccedenza)} a credito` }[data.caso];
      toast.success(`${msg} · Ricevuta ${data.ricevuta_numero}`);
      if (data.cf_mancante) toast.warning("Codice Fiscale mancante per questo beneficiario: la ricevuta non lo riporterà. Aggiungilo nell'anagrafica prima di inviarla.", { duration: 7000 });
      const benId = incasso.ben_id, benName = incasso.ben_name;
      setIncasso(null); load();
      let email = "";
      try { const { data: b } = await api.get(`/et/beneficiari/${benId}`); email = b.email || ""; } catch { /* noop */ }
      setJustPaid({ ricevuta_id: data.ricevuta_id, ricevuta_numero: data.ricevuta_numero, ben_name: benName, importo: Number(inc.importo), cf_mancante: data.cf_mancante });
      setPaidTo(email);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const inviaRicevutaPaid = async () => {
    if (!justPaid) return;
    setSendingPaid(true);
    try {
      const { data } = await api.post(`/et/ricevute/${justPaid.ricevuta_id}/invia-email`, { email: paidTo });
      toast.success(`Ricevuta inviata a ${data.email}`);
      setJustPaid(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSendingPaid(false); }
  };

  const sollecita = async (q) => {
    try { const { data } = await api.post(`/et/quote/${q.quota_id}/sollecita`); toast.success(`Sollecito ${data.livello}° registrato`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const inviaSollecitoEmail = async (q) => {
    try { const { data } = await api.post(`/et/quote/${q.quota_id}/sollecito-email`, {}); toast.success(`Sollecito inviato a ${data.email}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doAnnulla = async () => {
    try {
      const { data } = await api.post(`/et/quote/${annulla.quota_id}/annulla-sollecito`, { motivo });
      toast.success(`Sollecito annullato · stato: ${data.stato}`);
      setAnnulla(null); setMotivo("Sollecito inserito per errore"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <ETLayout subtitle="Quote & Incassi" title="Quote"
      actions={
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="et-genera-singola-btn" variant="outline" onClick={openSingola}><UserPlus className="h-4 w-4 mr-2" /> Genera singola</Button>
          <Button data-testid="et-genera-btn" onClick={openGen} className="brand-gradient text-white border-0"><Layers className="h-4 w-4 mr-2" /> Genera quote mese</Button>
        </div>
      }>
      <div className="flex gap-3 mb-4 flex-wrap items-end">
        <div><Label className="text-slate-600 text-sm">Competenza</Label><Input type="month" value={competenza} onChange={(e) => setCompetenza(e.target.value)} className="w-40 bg-white border-slate-300" data-testid="et-quote-month" /></div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Cerca beneficiario…" className="pl-9 bg-white border-slate-300" data-testid="et-quote-search" />
        </div>
        <Select value={statoFilter} onValueChange={setStatoFilter}>
          <SelectTrigger className="w-44" data-testid="et-quote-stato"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="DA_PAGARE">Da pagare</SelectItem>
            <SelectItem value="PARZIALE">Parziale</SelectItem>
            <SelectItem value="PAGATO">Pagato</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load}>Filtra</Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Beneficiario</TableHead><TableHead>Comp.</TableHead><TableHead>Quota</TableHead>
            <TableHead>Pagato</TableHead><TableHead>Residuo</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">Nessuna quota per questa competenza.</TableCell></TableRow>
            ) : items.map((q) => (
              <TableRow key={q.quota_id} data-testid={`et-quota-row-${q.quota_id}`}>
                <TableCell><div className="font-medium text-slate-900">{q.ben_name}{q.is_residuo && <span className="text-[10px] ml-1 text-amber-600">(residuo)</span>}</div><div className="text-xs text-slate-500">{q.centro_name}</div></TableCell>
                <TableCell className="text-sm">{q.competenza}</TableCell>
                <TableCell className="text-sm">{euro(q.quota)}{q.credito_applicato > 0 && <div className="text-[11px] text-emerald-600">-{euro(q.credito_applicato)} credito</div>}</TableCell>
                <TableCell className="text-sm">{euro(q.importo_pagato)}</TableCell>
                <TableCell className="text-sm font-medium">{euro(q.residuo)}</TableCell>
                <TableCell><QuotaBadge stato={q.stato} />
                  {q.sollecito_email_inviato && <div className="mt-1"><span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5" data-testid={`et-sollecito-inviato-${q.quota_id}`} title={q.sollecito_email_data ? `Inviato il ${new Date(q.sollecito_email_data).toLocaleDateString("it-IT")}${q.sollecito_email_to ? ` a ${q.sollecito_email_to}` : ""}` : "Sollecito inviato via email"}><Mail className="h-3 w-3" />Inviato Sollecito</span></div>}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {q.stato !== "PAGATO" && <Button size="sm" onClick={() => openIncasso(q)} className="brand-gradient text-white border-0 h-8" data-testid={`et-incassa-${q.quota_id}`}><Wallet className="h-3.5 w-3.5 mr-1" /> Incassa</Button>}
                  {["DA_PAGARE", "PARZIALE", "PRIMO_SOLLECITO", "SECONDO_SOLLECITO"].includes(q.stato) && <Button size="sm" variant="ghost" className="h-8" onClick={() => sollecita(q)} data-testid={`et-sollecita-${q.quota_id}`} title="Genera/escalation sollecito"><Send className="h-3.5 w-3.5 mr-1" /> Sollecita</Button>}
                  {q.sollecito_livello > 0 && <Button size="sm" variant="outline" className="h-8 ml-1" onClick={() => window.open(`${API}/et/quote/${q.quota_id}/sollecito-pdf`, "_blank")} data-testid={`et-sollecito-apri-${q.quota_id}`} title="Apri il PDF del sollecito per verificarlo"><FileText className="h-3.5 w-3.5 mr-1" /> Apri</Button>}
                  {q.sollecito_livello > 0 && <Button size="sm" className={`h-8 ml-1 border-0 text-white ${q.sollecito_email_inviato ? "bg-slate-400 hover:bg-slate-500" : "bg-blue-600 hover:bg-blue-700"}`} onClick={() => inviaSollecitoEmail(q)} data-testid={`et-sollecito-email-${q.quota_id}`} title="Invia il sollecito via email al beneficiario"><Mail className="h-3.5 w-3.5 mr-1" /> {q.sollecito_email_inviato ? "Reinvia" : "Invia"}</Button>}
                  {q.sollecito_livello > 0 && <Button size="sm" variant="ghost" className="h-8 ml-1 text-amber-600 hover:text-amber-700" onClick={() => { setAnnulla(q); setMotivo("Sollecito inserito per errore"); }} data-testid={`et-annulla-sollecito-${q.quota_id}`} title="Annulla sollecito"><Undo2 className="h-3.5 w-3.5" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Genera massiva — anteprima di sicurezza */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Genera quote del mese</DialogTitle></DialogHeader>

          {genStep === "input" && (
            <>
              <div className="py-2 space-y-2">
                <Label className="text-slate-600 text-sm">Competenza</Label>
                <Input type="month" value={genMonth} onChange={(e) => setGenMonth(e.target.value)} data-testid="et-gen-month" />
                <p className="text-xs text-slate-500">Prima di generare, il sistema analizza il mese scelto e mostra un'anteprima: quote già incassate, insolventi e quote da creare. Nessuna quota viene creata finché non confermi.</p>
              </div>
              <DialogFooter><Button onClick={analizzaMassiva} disabled={genLoading} className="brand-gradient text-white border-0" data-testid="et-gen-analizza">{genLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Analizza mese"}</Button></DialogFooter>
            </>
          )}

          {genStep === "preview" && genPreview && (
            <div className="py-2 space-y-3 text-sm" data-testid="et-gen-preview">
              {genPreview.mese_gia_lavorato && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-amber-800" data-testid="et-gen-warning">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Il mese <b>{genPreview.competenza}</b> è già stato lavorato ({genPreview.gia_emesse_totali} quote emesse). Verranno create solo le quote mancanti, senza toccare quelle esistenti.</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Stat label="Beneficiari attivi" value={genPreview.beneficiari_attivi} />
                <Stat label="Già emesse" value={genPreview.gia_emesse_totali} />
                <Stat label="Già incassate" value={genPreview.gia_incassate.length} cls="text-emerald-600" />
                <Stat label="Insolventi (non toccate)" value={genPreview.insolventi.length} cls="text-red-600" />
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-emerald-800">Quote da creare ora</span>
                  <span className="font-heading text-lg text-emerald-700" data-testid="et-gen-dacreare-count">{genPreview.da_creare.length}</span>
                </div>
                <div className="text-xs text-emerald-700 mt-1">Importo previsto: {euro(genPreview.importo_da_creare)}</div>
              </div>
              {genPreview.da_creare.length > 0 ? (
                <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="et-gen-dacreare-list">
                  {genPreview.da_creare.map((x) => (
                    <div key={x.ben_id} className="flex justify-between px-3 py-1.5 text-xs">
                      <span className="text-slate-700">{x.ben_name}<span className="text-slate-400"> · {x.centro_name}</span></span>
                      <span className="font-medium text-slate-900">{euro(x.quota_prevista)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-2">Nessuna quota da creare: tutti i beneficiari attivi hanno già una quota per questo mese.</p>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setGenStep("input")}>Indietro</Button>
                <Button onClick={confermaMassiva} disabled={genLoading || genPreview.da_creare.length === 0} className="brand-gradient text-white border-0" data-testid="et-gen-conferma">
                  {genLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : `Conferma e crea ${genPreview.da_creare.length} quote`}
                </Button>
              </DialogFooter>
            </div>
          )}

          {genStep === "result" && genResult && (
            <div className="py-2 space-y-2 text-sm" data-testid="et-gen-result">
              <div className="flex items-center gap-2 text-emerald-600 font-medium"><CheckCircle2 className="h-5 w-5" /> Generazione completata</div>
              <Row k="Quote create ora" v={genResult.quote_create} />
              <Row k="Quote già emesse (non toccate)" v={genResult.quote_gia_emesse} />
              <Row k="Crediti applicati" v={euro(genResult.crediti_applicati)} />
              <Row k="Importo totale generato" v={euro(genResult.importo_totale_generato)} />
              <DialogFooter><Button onClick={() => setGenOpen(false)} className="brand-gradient text-white border-0">Chiudi</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Genera singola */}
      <Dialog open={singOpen} onOpenChange={setSingOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Genera quota singola</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-xs text-slate-500">Crea la quota di un singolo beneficiario per un mese specifico (utile per correggere una quota mancante di un mese passato). Se la quota esiste già viene bloccata.</p>
            <div className="space-y-1.5">
              <Label className="text-slate-600 text-sm">Beneficiario</Label>
              <Select value={singBen} onValueChange={setSingBen}>
                <SelectTrigger data-testid="et-sing-ben"><SelectValue placeholder="Seleziona beneficiario…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {beneficiari.map((b) => (
                    <SelectItem key={b.ben_id} value={b.ben_id}>{b.cognome} {b.nome}{b.centro_name ? ` · ${b.centro_name}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-600 text-sm">Competenza</Label>
              <Input type="month" value={singMonth} onChange={(e) => setSingMonth(e.target.value)} data-testid="et-sing-month" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSingOpen(false)}>Annulla</Button>
            <Button onClick={generaSingola} disabled={!singBen || singLoading} className="brand-gradient text-white border-0" data-testid="et-sing-confirm">
              {singLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea quota"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Incasso */}
      <Dialog open={!!incasso} onOpenChange={(o) => !o && setIncasso(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Incassa quota — {incasso?.ben_name}</DialogTitle></DialogHeader>
          {incasso && (
            <div className="py-2 space-y-3">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm flex justify-between">
                <span className="text-slate-500">Dovuto</span>
                <span className="font-medium text-slate-900">{euro(incasso.quota - (incasso.importo_pagato || 0))}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-slate-600 text-sm">Importo ricevuto (€)</Label><Input type="number" value={inc.importo} onChange={(e) => setInc({ ...inc, importo: e.target.value })} data-testid="et-inc-importo" /></div>
                <div className="space-y-1.5"><Label className="text-slate-600 text-sm">Data</Label><Input type="date" value={inc.data} onChange={(e) => setInc({ ...inc, data: e.target.value })} /></div>
                <div className="space-y-1.5"><Label className="text-slate-600 text-sm">Metodo</Label>
                  <Select value={inc.metodo} onValueChange={(v) => setInc({ ...inc, metodo: v })}>
                    <SelectTrigger data-testid="et-inc-metodo"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="bonifico">Bonifico</SelectItem><SelectItem value="contanti">Contanti</SelectItem><SelectItem value="carta">Carta</SelectItem><SelectItem value="altro">Altro</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1.5"><Label className="text-slate-600 text-sm">Note</Label><Textarea value={inc.note} onChange={(e) => setInc({ ...inc, note: e.target.value })} /></div>
              </div>
              <p className="text-xs text-slate-500">Il sistema gestisce automaticamente pagamenti parziali (crea il residuo) ed eccedenze (credito al mese successivo), e genera la ricevuta PDF.</p>
            </div>
          )}
          <DialogFooter><Button onClick={doIncasso} disabled={!inc.importo} className="brand-gradient text-white border-0" data-testid="et-inc-confirm">Registra incasso</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Annulla sollecito */}
      <Dialog open={!!annulla} onOpenChange={(o) => !o && setAnnulla(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Annulla sollecito — {annulla?.ben_name}</DialogTitle></DialogHeader>
          {annulla && (
            <div className="py-2 space-y-3">
              <p className="text-sm text-slate-600">La quota tornerà allo stato <b>da incassare</b> e il livello di sollecito verrà azzerato. Indica la motivazione (verrà registrata nel Log).</p>
              <div className="space-y-1.5">
                <Label className="text-slate-600 text-sm">Motivazione</Label>
                <Select value={motivo} onValueChange={setMotivo}>
                  <SelectTrigger data-testid="et-annulla-motivo-preset"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Sollecito inserito per errore">Sollecito inserito per errore</SelectItem>
                    <SelectItem value="Pagamento già ricevuto">Pagamento già ricevuto</SelectItem>
                    <SelectItem value="Accordo con la famiglia">Accordo con la famiglia</SelectItem>
                    <SelectItem value="Altro">Altro (specifica sotto)</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="et-annulla-motivo" placeholder="Motivazione…" className="mt-1" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnnulla(null)}>Annulla</Button>
            <Button onClick={doAnnulla} className="bg-amber-500 hover:bg-amber-600 text-white border-0" data-testid="et-annulla-confirm">Conferma annullo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Invio ricevuta subito dopo l'incasso */}
      <Dialog open={!!justPaid} onOpenChange={(o) => !o && setJustPaid(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Incasso registrato ✓</DialogTitle></DialogHeader>
          {justPaid && (
            <div className="py-2 space-y-3">
              <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium"><CheckCircle2 className="h-5 w-5" /> Ricevuta {justPaid.ricevuta_numero} generata per {justPaid.ben_name}.</div>
              {justPaid.cf_mancante && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800 flex items-start gap-1" data-testid="et-paid-cf-missing">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> <span><b>Codice Fiscale mancante</b>: la ricevuta non lo riporta. Aggiungilo nell'anagrafica del beneficiario e rigenera/reinvia la ricevuta.</span>
                </div>
              )}
              <p className="text-sm text-slate-600">Vuoi inviare subito la ricevuta via email al pagante?</p>
              <div className="space-y-1.5">
                <Label className="text-slate-600 text-sm">Email destinatario</Label>
                <Input type="email" value={paidTo} onChange={(e) => setPaidTo(e.target.value)} placeholder="email del pagante…" data-testid="et-paid-email" />
              </div>
              {!emailCfg.configured && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800 flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" /> La casella email non è ancora collegata: l'invio si attiverà quando verrà inserita la Password per le app.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setJustPaid(null)}>Chiudi</Button>
            <Button onClick={inviaRicevutaPaid} disabled={sendingPaid || !paidTo} className="brand-gradient text-white border-0" data-testid="et-paid-send">
              {sendingPaid ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-2" /> Invia ricevuta</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ETLayout>
  );
}

const Row = ({ k, v }) => (<div className="flex justify-between border-b border-slate-100 py-1.5"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-900">{v}</span></div>);
const Stat = ({ label, value, cls = "text-slate-900" }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
    <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`font-heading text-lg ${cls}`}>{value}</div>
  </div>
);
